import React from 'react';
import { Box, ThemeProvider, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SignupForm } from './SignupForm';
import { theme } from '../../style/theme';
import datavLogo from '../../Images/datav_logo.png';
import signupImage from '../../Images/signup_image.png';
import { motion } from 'framer-motion';
import signupIcon1 from '../../Images/signup_1.png';
import signupIcon2 from '../../Images/signup_2.png';
import signupIcon3 from '../../Images/signup_3.png';
import signupIcon4 from '../../Images/signup_4.png';
import signupIcon5 from '../../Images/signup_5.png';
import { PrimaryFooter } from '../../components/ui/footer';

export function SignupPage() {
  const navigate = useNavigate();

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: '#e8fafc',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '100%',
            p: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 1, sm: 1, md: 1 },
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
          }}
        >
          <Box
            component="img"
            src={datavLogo}
            alt="Dataverse"
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer',
              height: 'auto',
              maxWidth: { xs: '90px', sm: '115px', md: '135px' },
              width: { xs: '54px', sm: '72px', md: '85px' },
              objectFit: 'contain',
              display: 'block',
              imageRendering: '-webkit-optimize-contrast',
              WebkitImageRendering: 'crisp-edges',
              msImageRendering: 'crisp-edges',
              backfaceVisibility: 'hidden',
              transform: 'translateZ(0)',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              '&:hover': {
                opacity: 0.8,
              },
              transition: 'opacity 0.2s ease',
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 0, sm: 0 },
          }}
        >
          <Box
            sx={{
              flex: { xs: 0, sm: 1 },
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              justifyContent: 'flex-start',
              position: 'relative',
              p: { sm: 3, md: 4 },
              pr: { sm: 1.5, md: 2 },
              pt: { sm: 6, md: 8 },
            }}
          >
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: '100%', gap: { xs: 1.5, sm: 1.5, md: 2 } }}>
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.375rem', lg: '1.5rem' },
                  color: '#383838',
                  mb: { xs: 0.5, sm: 0.75, md: 0.75, lg: 1 },
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  width: '100%',
                }}
              >
                Build powerful data pipelines without complexity.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap',
                  gap: { xs: 1.5, sm: 1.5, md: 2 },
                  mb: { xs: 3, sm: 3.5, md: 4 },
                  padding: 0,
                  margin: 0,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {[
                  { icon: signupIcon1, text: 'Connect your tools' },
                  { icon: signupIcon2, text: 'Transform into usable data' },
                  { icon: signupIcon3, text: 'Automate data flows' },
                  { icon: signupIcon4, text: 'Free 30-day trial' },
                  { icon: signupIcon5, text: 'Quick setup' },
                ].map((feature, index) => {
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                        padding: 0,
                        margin: 0,
                        flexShrink: 0,
                        minWidth: 0,
                        boxSizing: 'border-box',
                      }}
                    >
                      <Box
                        component="img"
                        src={feature.icon}
                        alt={feature.text}
                        sx={{
                          flexShrink: 0,
                          width: { xs: '18px', sm: '19px', md: '19px', lg: '20px' },
                          height: { xs: '18px', sm: '19px', md: '19px', lg: '20px' },
                          objectFit: 'contain',
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: '"IBM Plex Sans", sans-serif',
                          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.8125rem', lg: '0.8525rem' },
                          color: '#4A5565',
                          fontWeight: 500,
                          lineHeight: '100%',
                          letterSpacing: '-0.31px',
                          whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        }}
                      >
                        {feature.text}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                <Box
                  component="img"
                  src={signupImage}
                  alt="Data pipeline diagram"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxWidth: '90%',
                    objectFit: 'contain',
                    display: 'block',
                    borderRadius: '8px',
                    imageRendering: '-webkit-optimize-contrast',
                    WebkitImageRendering: 'crisp-edges',
                    msImageRendering: 'crisp-edges',
                    backfaceVisibility: 'hidden',
                    transform: 'translateZ(0)',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                  }}
                />
              </Box>
            </Box>

          </Box>

          <Box
            sx={{
              flex: { xs: 1, sm: 0.75 },
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'flex-start', lg: 'center' },
              justifyContent: 'center',
              p: { xs: 1.5, sm: 2, md: 3 },
              pl: { sm: 1.5, md: 2 },
              pb: { xs: 3, sm: 4, md: 4 },
              pt: { xs: 1, sm: 2, md: 3 },
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '556px', md: '556px' },
                boxSizing: 'border-box',
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                style={{ 
                  width: '100%', 
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  p: { xs: 2, sm: 2.5, md: 3 },
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                }}
              >
                <SignupForm />
              </Paper>
              </motion.div>
            </Box>
          </Box>
        </Box>
      </Box>
      <PrimaryFooter />
    </ThemeProvider>
  );
}

export default SignupPage;
